import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import {
  createSubCategory,
  deleteSubCategory,
  getAllSubCategorys,
  updateSubCategory,
} from "../../service/categoryService";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AddIcon from "@mui/icons-material/Add";
import Color from "../../components/Color";
import SubCategory from "./AddSubCategory";
import { useSnackbar } from "../../App"; // Import useSnackbar hook

const SubCategories = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Detects screens smaller than 'sm' (600px)
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs")); // Custom breakpoint for very small screens (<400px)
  const { showSnackbar } = useSnackbar(); // Use the snackbar hook

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [searchCategory, setSearchCategory] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortModel, setSortModel] = useState([]);
  const [openSubCategoryModal, setOpenSubCategoryModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [formData, setFormData] = useState({
    category_id: "",
    name: "",
    description: "",
    is_active: true,
  });

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const fetchSubCategories = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const sortField = sortModel[0]?.field || "id";
      const sortOrder = sortModel[0]?.sort || "desc";

      const res = await getAllSubCategorys(
        page,
        limit,
        debouncedSearch || "",
        sortField,
        sortOrder
      );

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        const mapped = data.data.map((item) => ({
          ...item,
          categoryName: item.category?.name || "—",
        }));
        setRows(mapped);
        setRowCount(data.total);
      } else {
        setRows([]);
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch sub categories!", "error");
    }
    setLoading(false);
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(searchCategory);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchCategory]);

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteSubCategory(deleteId);
      showSnackbar("Sub category deleted successfully!", "success");
      fetchSubCategories();
    } catch {
      showSnackbar("Failed to delete sub category!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleSubCatSubmit = async () => {
    try {
      let res;
      if (editingId) {
        res = await updateSubCategory(editingId, formData);
      } else {
        res = await createSubCategory(formData);
      }
      fetchSubCategories();
      return res.data;
    } catch (error) {
      console.error("Save Sub Category Error:", error);
      throw error;
    }
  };

  const handleEdit = (category) => {
    setFormData({
      id: category.id,
      category_id: category.category_id || "",
      name: category.name || "",
      description: category.description || "",
      is_active: category.is_active ?? true,
    });
    setEditingId(category.id);
    setOpenSubCategoryModal(true);
    handleMenuClose();
  };

  // DataGrid Columns: Hide categoryName and description on mobile to prevent overflow
  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      minWidth: isMobile ? 80 : 150,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.04)',
            borderRadius: 1.5,
            padding: isMobile ? '10px' : '8px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.08)',
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "name", headerName: "Sub Category", flex: 1.5, minWidth: isMobile ? 120 : 200 },

    { field: "categoryName", headerName: "Category", flex: 1.5, minWidth: 200 },
    { field: "description", headerName: "Description", flex: 2, minWidth: 200 },

  ];

  return (
    <Box sx={{ padding: isMobile ? 1 : 0, mb: { xs: 0 } }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <TextField
          value={searchCategory}
          onChange={(e) => setSearchCategory(e.target.value)}
          placeholder="Search sub category"
          variant="outlined"
          InputLabelProps={{ shrink: false }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
              </InputAdornment>
            ),
            endAdornment: searchCategory && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchCategory("");
                    setDebouncedSearch("");
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                  }}
                >
                  ✖
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputLabel-root": { display: "none" },
            "& .MuiOutlinedInput-root": {
              "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
              height: isMobile ? "34px" : "37.69px", // Smaller height on mobile
              fontSize: isMobile ? "0.85rem" : "1rem",
            },
            width: isMobile ? "100%" : 250, // Full width on mobile
            mb: isMobile ? 0 : 0,
          }}
        />
        <Button
          sx={{
            background: Color.primary.main,
            color: "white",
            height: isMobile ? "34px" : "37.69px",
            width: isMobile ? "100%" : 240, // Full width on mobile
            fontSize: isMobile ? "0.85rem" : "1rem",
            mt: isMobile ? 0 : 0,
            "&:hover": {
              background: Color.primary.dark,
            },
          }}
          onClick={() => {
            setEditingId(null);
            setFormData({ id: null, category_id: "", name: "", description: "", is_active: true });
            setOpenSubCategoryModal(true);
          }}
          startIcon={<AddIcon />}
        >
          Add Sub Category
        </Button>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 2 : 2 }}>
        <DataGrid
          rows={rows}
          columns={columns.map((col) => ({
            ...col,
            flex: col.flex || 1,
            minWidth: col.minWidth || (isMobile ? 80 : 100), // Smaller minWidth on mobile
          }))}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={rowCount}
          rowHeight={isMobile ? 48 : 56} // Smaller row height on mobile
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu
              disableColumnSorting
          getRowId={(row) => row.id}
          autoHeight
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "16px", // Smaller header font on mobile
              fontWeight: 600,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px", // Smaller cell font on mobile
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto", // Prevent horizontal scrolling on mobile
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200, // Full width on mobile
          },
        }}
      >
        <MenuItem
          onClick={() => handleEdit(selectedRow)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDeleteClick(selectedRow?.id)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
      </Menu>

      <SubCategory
        open={openSubCategoryModal}
        onClose={() => {
          setOpenSubCategoryModal(false);
          setEditingId(null);
        }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubCatSubmit}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs" // Smaller dialog on mobile
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Delete Confirmation
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the sub category <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row", // Stack buttons vertically on mobile
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubCategories;